import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plane, ArrowRight } from "lucide-react";

const MAP_IMG =
  "/assets/carte-du-monde.webp";

const COUNTRIES = [
  "Lyon", "Gabon", "Bénin", "Tunisie", "Tchad", "Sénégal", "Inde",
];

export const CampusWorld = () => {
  const navigate = useNavigate();
  return (
    <section
      id="campus-monde"
      data-testid="campus-world-section"
      className="section-padding bg-white relative overflow-hidden"
    >

      <div className="container-edge relative z-10">
        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Text block */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="overline mb-5 flex items-center gap-3">
              <Plane className="h-4 w-4" strokeWidth={1.6} />
              Mobilité internationale
            </div>

            <div className="flex items-baseline gap-3 sm:gap-4 mb-4 flex-wrap">
              <motion.span
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, type: "spring", stiffness: 120 }}
                className="stat-num text-6xl sm:text-7xl md:text-9xl text-navy-700 leading-none"
                data-testid="campus-world-count"
              >
                7
              </motion.span>
              <span className="font-display text-xl sm:text-2xl md:text-3xl text-navy-700 leading-tight max-w-[14ch]">
                campus à <em className="not-italic red-shine">l'étranger</em>
              </span>
            </div>

            <div className="red-rule mb-6" />

            <p className="text-[#444] text-[16px] md:text-[17px] leading-[1.7] max-w-xl mb-8">
              Ouvrez-vous au monde en partant étudier chaque année sur un de nos
              campus partenaires. Une semaine, un semestre ou une année complète,
              vivez l'international sans rupture pédagogique.
            </p>

            {/* Country chips */}
            <ul className="flex flex-wrap gap-2 mb-10" data-testid="campus-world-list">
              {COUNTRIES.map((c, i) => (
                <motion.li
                  key={c}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className="px-3 py-1.5 text-[12px] uppercase tracking-[0.14em] text-navy-700 border border-navy-100 bg-white hover:border-navy-500 transition-colors"
                >
                  {c}
                </motion.li>
              ))}
            </ul>


          </motion.div>

          {/* Right: Map */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 lg:col-span-7 relative"
          >
            <div className="relative group cursor-pointer">
              {/* Decorative frame */}
              <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-navy-500 pointer-events-none group-hover:border-red-500 transition-colors duration-500" aria-hidden="true" />
              <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-navy-500 pointer-events-none group-hover:border-red-500 transition-colors duration-500" aria-hidden="true" />

              {/* Glow overlay on hover */}
              <div className="absolute inset-0 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at center, rgba(237,41,57,0.08) 0%, transparent 70%)" }} />

              {/* Zoom + brightness effect */}
              <div className="overflow-hidden">
                <motion.img
                  src={MAP_IMG}
                  alt="Carte des 7 campus ECL dans le monde"
                  className="w-full h-auto relative z-10 transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                  data-testid="campus-world-map"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>

              {/* Label apparu au hover */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                <span className="bg-navy-700/90 backdrop-blur-sm text-white text-[10px] uppercase tracking-[0.22em] font-semibold px-4 py-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  7 campus à l'international
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CampusWorld;
